import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { deleteProveedor, getProveedores } from '../../lib';
import type { Proveedor } from '../../lib/appTypes';
import { SectionSurface } from '../../components/role-ui';
import FormProveedor from './FormProveedor';
import { Modal, confirmar, SkeletonRows } from '../../components/ui-kit';

function PanelPopup({
  children,
  onClose,
}: {
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} maxWidthClassName="max-w-4xl">
      <div className="px-6 pb-6 pt-14">{children}</div>
    </Modal>
  );
}

function formaPagoLabel(value?: Proveedor['formaPagoSri']): string {
  if (value === 'TRANSFERENCIA_EFECTIVO') return 'Transferencia / Efectivo';
  if (value === 'TARJETA_DEBITO_CREDITO') return 'Tarjeta débito / crédito';
  if (value === 'OTROS') return 'Otros';
  return '—';
}

function proveedorTipoLabel(proveedor: Proveedor): string {
  return proveedor.esPropia ? 'Propio' : 'Externo';
}

export default function BodegaProveedoresPanel() {
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState<Proveedor | null>(null);

  async function refresh() {
    try {
      setLoading(true);
      const response = await getProveedores({
        page_size: 500,
        order_by: 'nombre_fiscal',
        order_up: true,
      });
      setProveedores(response);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudieron cargar los proveedores.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const proveedoresFiltrados = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    if (!term) return proveedores;

    return proveedores.filter(proveedor => {
      const text = [
        proveedor.identificacion,
        proveedor.nombreFiscal,
        proveedor.ciudad,
        proveedor.direccion,
        proveedor.telefono,
        proveedor.email,
        proveedor.formaPagoSri,
        proveedor.esPropia ? 'propio' : 'externo',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return text.includes(term);
    });
  }, [busqueda, proveedores]);

  async function handleEliminarProveedor(proveedor: Proveedor) {
    if (!(await confirmar({ titulo: `¿Eliminar el proveedor ${proveedor.nombreFiscal}?`, mensaje: 'Esta acción no se puede deshacer.', textoAceptar: 'Eliminar' }))) return;

    try {
      await deleteProveedor(proveedor.id);
      await refresh();
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo eliminar el proveedor. Verifica si tiene insumos asociados.');
    }
  }

  return (
    <>
      <section className="space-y-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-end">
          <button
            type="button"
            onClick={() => {
              setEditingProveedor(null);
              setShowForm(true);
            }}
            className="ui-btn ui-btn-primary"
          >
            Nuevo proveedor
          </button>
        </div>

        {dataError && (
          <div className="rounded-[var(--radius-panel)] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-sm">
            {dataError}
          </div>
        )}

        <div className="grid gap-4 xl:grid-cols-3">
          <article className="ui-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Total proveedores</p>
            <p className="mt-4 text-3xl font-semibold text-stone-950">{proveedores.length}</p>
          </article>
          <article className="ui-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Propios</p>
            <p className="mt-4 text-3xl font-semibold text-stone-950">{proveedores.filter(item => item.esPropia).length}</p>
          </article>
          <article className="ui-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Filtrados</p>
            <p className="mt-4 text-3xl font-semibold text-stone-950">{proveedoresFiltrados.length}</p>
          </article>
        </div>

        <SectionSurface className="space-y-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h4 className="text-lg font-semibold text-stone-900">Listado de proveedores</h4>
              <p className="text-sm text-stone-500">Busca por identificación, nombre fiscal, ciudad, teléfono o email.</p>
            </div>
            <div className="w-full max-w-sm">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                Buscar proveedor
              </label>
              <input
                value={busqueda}
                onChange={event => setBusqueda(event.target.value)}
                placeholder="Nombre, RUC, ciudad..."
                className="ui-input"
              />
            </div>
          </div>

          {loading ? (
            <SkeletonRows etiqueta="Cargando proveedores..." />
          ) : proveedoresFiltrados.length === 0 ? (
            <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-10 text-center text-stone-500">
              No hay proveedores que coincidan con la búsqueda.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="ui-tabla min-w-full border-separate border-spacing-y-2 text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.18em] text-[var(--freisa-text-muted)]">
                    <th className="pb-2 pr-4 font-semibold">Proveedor</th>
                    <th className="pb-2 pr-4 font-semibold">Identificación</th>
                    <th className="pb-2 pr-4 font-semibold">Ciudad</th>
                    <th className="pb-2 pr-4 font-semibold">Contacto</th>
                    <th className="pb-2 pr-4 font-semibold">Tipo</th>
                    <th className="pb-2 pr-4 font-semibold">Pago SRI</th>
                    <th className="pb-2 font-semibold text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {proveedoresFiltrados.map(proveedor => (
                    <tr key={proveedor.id} className="bg-[var(--freisa-bg)]/70 text-[var(--freisa-text)]">
                      <td className="rounded-l-[16px] px-4 py-3">
                        <p className="font-semibold">{proveedor.nombreFiscal}</p>
                        <p className="mt-1 text-xs text-[var(--freisa-text-soft)]">{proveedor.direccion || 'Sin dirección'}</p>
                      </td>
                      <td className="px-4 py-3 text-[var(--freisa-text-soft)]">
                        <p>{proveedor.tipoIdentificacion}</p>
                        <p className="font-medium text-[var(--freisa-text)]">{proveedor.identificacion}</p>
                      </td>
                      <td className="px-4 py-3 text-[var(--freisa-text-soft)]">{proveedor.ciudad || '—'}</td>
                      <td className="px-4 py-3 text-[var(--freisa-text-soft)]">
                        <p>{proveedor.telefono || '—'}</p>
                        <p>{proveedor.email || '—'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          proveedor.esPropia
                            ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border border-amber-200 bg-amber-50 text-amber-700'
                        }`}>
                          {proveedorTipoLabel(proveedor)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full border border-stone-200 bg-white px-3 py-1 text-xs font-semibold text-stone-700">
                          {formaPagoLabel(proveedor.formaPagoSri)}
                        </span>
                      </td>
                      <td className="rounded-r-[16px] px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingProveedor(proveedor);
                              setShowForm(false);
                            }}
                            className="ui-btn ui-btn-secondary ui-btn-sm"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleEliminarProveedor(proveedor)}
                            className="ui-btn ui-btn-danger ui-btn-sm"
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionSurface>
      </section>

      {(showForm || editingProveedor) && (
        <PanelPopup
          onClose={() => {
            setShowForm(false);
            setEditingProveedor(null);
          }}
        >
          <FormProveedor
            initialProveedor={editingProveedor}
            onSaved={() => {
              setShowForm(false);
              setEditingProveedor(null);
              void refresh();
            }}
            onCancel={() => {
              setShowForm(false);
              setEditingProveedor(null);
            }}
          />
        </PanelPopup>
      )}
    </>
  );
}
